import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "hooks/use-selector";
import {
  selectCoverages,
  selectDateOfBirth,
  selectDestinations,
  selectEmail,
  selectHasTripCancellationCoverage,
  selectInitialTripCost,
  selectIsDirectOffer,
  selectIsRemarketing,
  selectIsTestUser,
  selectName,
  selectPolicyCodeHash,
  selectResidency,
  selectTotalPrice,
  selectTravelers,
  selectTripCost,
  selectTripDays,
  selectTripEndDate,
  selectTripStartDate,
} from "store/quote/selectors";
import { calculateAge } from "utils/birthdate";
import { dateFormatter } from "utils/formatters/date";
import { trackPageView } from "utils/tracking";
import { getGtmDataLayer, gtmTrack } from "utils/tracking/gtm";
import {
  track as iterableTrack,
  updateUser as iterableUpdateUser,
} from "utils/tracking/iterable";
import {
  identifyMixpanelUser,
  mixpanelTrack,
  updateMixpanelUser,
} from "utils/tracking/mixpanel";
import { getAdvisorId } from "utils/tracking/utm";
import { countNonAdultTravelers } from "utils/traveler";
import { formatDates } from "utils/tracking/utils";
import { useStatsigUser } from "@statsig/react-bindings";

export function trackCancelForAnyReasonDepositDate(event: string, date: Date) {
  mixpanelTrack(event, { initial_deposit_date: dateFormatter(date) });
}

export function useTracking() {
  const birthdate = useSelector(selectDateOfBirth);
  const destinations = useSelector(selectDestinations);
  const email = useSelector(selectEmail);
  const hasTripCancellation = useSelector(selectHasTripCancellationCoverage);
  const isDirectOffer = useSelector(selectIsDirectOffer);
  const isRemarketing = useSelector(selectIsRemarketing);
  const isTestUser = useSelector(selectIsTestUser);
  const name = useSelector(selectName);
  const policyCodeHash = useSelector(selectPolicyCodeHash);
  const residency = useSelector(selectResidency);
  const startDate = useSelector(selectTripStartDate);
  const endDate = useSelector(selectTripEndDate);
  const totalPrice = useSelector(selectTotalPrice);
  const travelers = useSelector(selectTravelers);
  const { updateUserAsync } = useStatsigUser();
  const days = useSelector(selectTripDays);

  const { pathname } = useLocation();

  const [previousTotalPrice, setPreviousTotalPrice] = useState(totalPrice);

  const dates = {
    ...formatDates(dayjs(startDate), dayjs(endDate)),
    trip_duration: days,
  };

  useEffect(() => {
    // Statsig
    if (email) {
      void updateUserAsync({
        userID: email,
        email,
      });
    }
    // Google Tag Manager

    getGtmDataLayer().push({
      address: residency && {
        city: residency.locality,
        country: residency.country,
        first_name: name?.firstName,
        last_name: name?.lastName,
        postal_code: residency.zipCode,
        region: residency.areaLevel1,
        street: `${residency.streetAndNumber} ${residency.apartment}`.trim(),
      },
      age: birthdate && calculateAge(birthdate),
      email,
    });

    gtmTrack("offer", {
      price: totalPrice,
    });

    // Mixpanel

    if (isDirectOffer && email) {
      void identifyMixpanelUser(email);
    }

    updateMixpanelUser({
      is_test_user: isTestUser,
    });

    trackPageView("Offer Page", pathname, {
      is_remarketing: !!isRemarketing,
      quote_hash: policyCodeHash,
      quote_price: totalPrice,
      variation:
        residency?.areaLevel1 === "NY" || hasTripCancellation
          ? "saw_trip_cost"
          : "didnt_see_trip_cost",
      residence_state: residency?.areaLevel1,
      ...dates,
    });

    // Iterable

    if (email) {
      const commonIterableFields = {
        $first_name: name?.firstName,
        $last_name: name?.lastName,
        destinations: destinations
          ?.map((destination) => destination?.label)
          .filter(Boolean),
        quote_hash: policyCodeHash,
      };

      iterableTrack({
        dataFields: {
          ...commonIterableFields,
          is_advisor_utm: !!getAdvisorId(),
          is_direct_offer: !!isDirectOffer,
          is_remarketing: !!isRemarketing,
          main_traveler_age: travelers[0]
            ? calculateAge(travelers[0].dateOfBirth)
            : null,
          all_travelers_ages:
            travelers?.map(({ dateOfBirth }) => calculateAge(dateOfBirth)) ??
            [],
          non_adult_travelers_count: countNonAdultTravelers(travelers),
          total_travelers_count: travelers.length,
        },
        email,
        eventName: "iterable_seen_offer_page",
      });

      iterableUpdateUser({
        dataFields: {
          ...commonIterableFields,
          $email: email,
          address: residency?.label ?? null,
          days,
        },
        email,
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Google Tag Manager

    if (totalPrice !== previousTotalPrice) {
      gtmTrack("price_updated", { price: totalPrice });
      setPreviousTotalPrice(totalPrice);
    }
  }, [previousTotalPrice, totalPrice]);
}

export function useTrackCtaPressed() {
  const coverages = useSelector(selectCoverages);
  const totalPrice = useSelector(selectTotalPrice);
  const initialTripCost = useSelector(selectInitialTripCost);
  const tripCost = useSelector(selectTripCost);

  return () => {
    const selectedAddons = coverages
      .filter((coverage) => !coverage.isBase && coverage.isSelected)
      .map((coverage) => coverage.title);

    updateMixpanelUser({
      selected_add_ons: selectedAddons,
    });

    if (tripCost && initialTripCost && tripCost !== initialTripCost) {
      const tripCostDiff = Math.floor(tripCost - initialTripCost);
      const eventName =
        tripCostDiff > 0
          ? "clicked_increase_trip_cost"
          : "clicked_decrease_trip_cost";
      mixpanelTrack(eventName, { trip_cost_diff: tripCostDiff });
    }

    mixpanelTrack("Clicked Offer CTA", {
      pressed_payment_CTA: "Bottom CTA",
      quote_price: totalPrice,
      selected_add_ons: selectedAddons,
      trip_cost: tripCost && Math.floor(tripCost),
    });
  };
}
